/**
 * Breadcrumb — mono uppercase trail with ink-3 hairlines between segments.
 * Last segment is rendered in accent + bold to match the radical artboard.
 *
 * Source: .agents/design/project/artboard-radical.jsx:107-114
 */

import type { ReactNode } from 'react';

export interface BreadcrumbSegment {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
}

interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  className?: string;
}

export function Breadcrumb({ segments, className = '' }: BreadcrumbProps) {
  if (segments.length === 0) return null;
  return (
    <nav
      aria-label="Drobečková navigace"
      className={
        'flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 ' +
        className
      }
    >
      {segments.map((s, i) => {
        const isLast = i === segments.length - 1;
        const inner = isLast ? (
          <b className="font-semibold text-accent-text" aria-current="page">
            {s.label}
          </b>
        ) : s.href ? (
          <a href={s.href} onClick={s.onClick} className="hover:text-ink">
            {s.label}
          </a>
        ) : s.onClick ? (
          <button
            type="button"
            onClick={s.onClick}
            className="cursor-pointer bg-transparent p-0 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink"
          >
            {s.label}
          </button>
        ) : (
          <span>{s.label}</span>
        );
        return (
          <span key={i} className="flex items-center gap-3">
            {inner}
            {!isLast && <span aria-hidden className="block h-px w-[18px] bg-line-2" />}
          </span>
        );
      })}
    </nav>
  );
}
